import { useSnackbar } from "notistack";
import { useCallback, useEffect, useMemo, useState } from "react";

import { ScheduledJob, ScheduledJobBase } from "../../../../dtos/scheduledJob";
import { useApi } from "../../../useApi";
import { AjaxResult } from "../../../../enums/ajaxResult";

export function useEditScheduledJob(
    job: ScheduledJob,
    onJobSaved: (job: ScheduledJob) => void,
): {
    save: () => void;
    loading: boolean;
    jobBase: ScheduledJobBase;
    setJobBase: (jobBase: ScheduledJobBase) => void;
    isModified: boolean;
    initialJobBase: ScheduledJobBase;
} {
    const { scheduledJobsApi } = useApi();
    const { enqueueSnackbar } = useSnackbar();
    const initialJobBase: ScheduledJobBase = useMemo(() => ({
        name: job.name,
        type: job.type,
        targets: job.targets,
        data: job.data,
    }), [job]);
    const [jobBase, setJobBase] = useState<ScheduledJobBase>(initialJobBase);
    const [isModified, setIsModified] = useState(false);

    const [loading, setLoading] = useState(false);

    useEffect(() => {
        setIsModified(
            jobBase.type !== initialJobBase.type
            || jobBase.name !== initialJobBase.name
            || JSON.stringify(jobBase.targets) !== JSON.stringify(initialJobBase.targets)
            || JSON.stringify(jobBase.data) !== JSON.stringify(initialJobBase.data)
        )
    }, [jobBase, initialJobBase]);

    const save = useCallback(() => {
        setLoading(true);
        scheduledJobsApi.put(job.id, jobBase).then(({ result }) => {
            if (result === AjaxResult.Success) {
                onJobSaved({ ...job, ...jobBase });
            } else {
                enqueueSnackbar("Failed to save edited job", { variant: "error" });
            }
            setLoading(false);
        });
    }, [enqueueSnackbar, scheduledJobsApi, jobBase, job, onJobSaved]);

    return { save, loading, jobBase, setJobBase, initialJobBase, isModified };
}